import type { ButtonSize } from '../../../../../../mads/Button';
import type { ReportContentScreenNavigationProp } from '../../../../navigation/types';

import { useSubmitReport } from './ReportHooks';
import SubmitMessage from './SubmitMessage';
import { type BoxRef, Box, Button, Report } from '../../../../../../mads';
import { useI18n } from '../../../../../../shared/I18n';
import { logScreenView } from '../../../../../../shared/native-modules/tare/logScreenView';
import { logTapButton } from '../../../../../../shared/native-modules/tare/logTapButton';
import openLoginModal from '../../../../../../shared/native-modules/tare/openLoginModal';
import { useTareData } from '../../../../../../shared/native-modules/tare/TareDataContext';
import { useIsFullScreen } from '../../../../../../shared/util/ScreenType';
import { useViewjobOneGraphModel } from '../../../../context/ViewJobDataContext';
import { useIsAfsauthRajRequireLoginActiveForJob } from '../../../../hooks/useAfsauthRajRequireLoginTst';
import useFocusOnNavigation from '../../../../hooks/useFocusOnNavigation';
import { ViewJobDivider } from '../../Divider';
import PassportLoginModal from '../../JobComponent/ViewJobButtonLink/PassportLoginModal';

import { spacing } from '@indeed/ifl-tokens/react-native';
import { useNavigation } from '@react-navigation/native';
import { useCallback, useRef, useState } from 'react';
import { Platform, StyleSheet } from 'react-native';

const styles = StyleSheet.create({
    container: {
        rowGap: spacing.s2,
    },
    topDivider: {
        marginTop: spacing.s5,
        marginBottom: spacing.s5,
    },
    topSpacer: {
        marginTop: spacing.s5,
    },
});

const AUTH_GATE_SCREEN_NAME = 'rn-view-job-report-job';

interface ReportContentButtonProps {
    topSpacerMode?: 'none' | 'divider' | 'spacer';
    buttonSize?: ButtonSize | undefined;
    marginHorizontal?: number;
}

export function ReportContentButton({
    topSpacerMode = 'none',
    buttonSize = 'lg',
    marginHorizontal = 16,
}: ReportContentButtonProps) {
    const { gettext } = useI18n();

    const viewjobOneGraphModel = useViewjobOneGraphModel();
    const reportContentModel = viewjobOneGraphModel?.reportContent;

    const navigation = useNavigation<ReportContentScreenNavigationProp>();
    const reportContentStatus = useSubmitReport(reportContentModel);
    const withTopSafeArea = useIsFullScreen();
    const tareData = useTareData();
    const isLoggedIn = tareData?.accountStatus?.isLoggedIn ?? false;

    const isRAJLoginRequiredActive = useIsAfsauthRajRequireLoginActiveForJob(
        viewjobOneGraphModel?.job?.jobCountry,
    );

    const buttonRef = useRef<BoxRef | null>(null);
    const didNavigateToReportModal = useRef(false);
    const [isWebLoginModalOpen, setIsWebLoginModalOpen] = useState(false);

    const contextualSignInPassportURL =
        viewjobOneGraphModel?.thirdPartyApplyCreateAccountModel?.contextualSignInPassportURL ?? '';

    const openReportContent = useCallback(() => {
        didNavigateToReportModal.current = true;
        navigation.navigate('ReportContent', {
            isOpen: true,
            showPhotoLabel: false,
            withTopSafeArea,
        });
    }, [navigation, withTopSafeArea]);

    const handleWebAuthSuccess = useCallback(() => {
        setIsWebLoginModalOpen(false);
        logScreenView(`${AUTH_GATE_SCREEN_NAME}-logged-in`).catch(console.error);
        openReportContent();
    }, [openReportContent]);

    const handleWebLoginExit = useCallback(() => {
        setIsWebLoginModalOpen(false);
        logScreenView(`${AUTH_GATE_SCREEN_NAME}-login-cancelled`).catch(console.error);
    }, []);

    const openLogin = useCallback(async () => {
        logTapButton(AUTH_GATE_SCREEN_NAME, 'open-login-modal').catch(console.error);

        if (Platform.OS === 'web') {
            setIsWebLoginModalOpen(true);
        } else {
            try {
                const result = await openLoginModal(false, 'viewjob');
                if (result === 'SUCCESS') {
                    logScreenView(`${AUTH_GATE_SCREEN_NAME}-logged-in`).catch(console.error);
                    openReportContent();
                }
            } catch {
                logScreenView(`${AUTH_GATE_SCREEN_NAME}-login-cancelled`).catch(console.error);
            }
        }
    }, [openReportContent]);

    const handlePress = useCallback(() => {
        if (isRAJLoginRequiredActive && !isLoggedIn) {
            void openLogin();
            return;
        }
        openReportContent();
    }, [isRAJLoginRequiredActive, isLoggedIn, openLogin, openReportContent]);

    useFocusOnNavigation(buttonRef, {
        focusAfterRender: () => didNavigateToReportModal.current,
        onFocused: () => (didNavigateToReportModal.current = false),
    });

    return (
        <>
            {topSpacerMode === 'divider' && <ViewJobDivider sx={styles.topDivider} />}
            <Box sx={[styles.container, topSpacerMode === 'spacer' && styles.topSpacer]}>
                <Button
                    ref={buttonRef}
                    onPress={handlePress}
                    variant="utility"
                    sx={{ marginHorizontal: marginHorizontal }}
                    size={buttonSize}
                    iconBefore={<Report color="rgb(45, 45, 45)" />}
                    testID="vj-report-job"
                >
                    {gettext('Report job', ['allow jobseekers to report a job'])}
                </Button>
                <SubmitMessage status={reportContentStatus} />
            </Box>
            {isRAJLoginRequiredActive && (
                <PassportLoginModal
                    isOpen={isWebLoginModalOpen}
                    onExit={handleWebLoginExit}
                    onAuthSuccess={handleWebAuthSuccess}
                    contextualSignInPassportURL={contextualSignInPassportURL}
                    fromParameter="viewjob-raj-intercept"
                />
            )}
        </>
    );
}
